/**
 * Milestones View Component
 * 
 * Displays tiered badges and milestone progress
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Screen } from '@/components/layout/Screen';
import { Heading, Body, Caption } from '@/components/ui/Text';
import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import { getUserStats, updateUserStatsFromTrips } from '@/services/userStats';
import type { UserStats } from '@/services/userStats';
import { fetchUserTrips } from '@/services/trips';

type MilestoneTier = 'bronze' | 'silver' | 'gold' | 'platinum';

type Milestone = {
  id: string;
  name: string;
  description: string;
  tiers: Array<{
    tier: MilestoneTier;
    target: number;
    current: number;
    unlocked: boolean;
  }>;
  icon: string;
};

export function MilestonesView() {
  const theme = useTheme();
  const router = useRouter();
  const handleClose = () => {
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/(tabs)/profile');
  };

  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [milestonesError, setMilestonesError] = useState<string | null>(null);

  useEffect(() => {
    loadMilestones();
  }, [firebaseUser?.uid]);

  const loadMilestones = async () => {
    if (!firebaseUser?.uid) return;

    try {
      setMilestonesError(null);
      setLoading(true);
      let userStats = await getUserStats(firebaseUser.uid);
      // Backfill userStats from trips if doc is missing but user has trips
      if (!userStats) {
        try {
          const { trips } = await fetchUserTrips(firebaseUser.uid);
          if (trips.length > 0) {
            userStats = await updateUserStatsFromTrips(firebaseUser.uid, trips);
          }
        } catch {
          // ignore
        }
      }
      setStats(userStats);

      if (userStats) {
        // Calculate milestones from stats
        const calculatedMilestones: Milestone[] = [
          {
            id: 'countries',
            name: 'Countries Club',
            description: 'Visit countries around the world',
            icon: 'globe',
            tiers: [
              { tier: 'bronze', target: 5, current: userStats.countriesVisited.length, unlocked: userStats.countriesVisited.length >= 5 },
              { tier: 'silver', target: 10, current: userStats.countriesVisited.length, unlocked: userStats.countriesVisited.length >= 10 },
              { tier: 'gold', target: 25, current: userStats.countriesVisited.length, unlocked: userStats.countriesVisited.length >= 25 },
              { tier: 'platinum', target: 50, current: userStats.countriesVisited.length, unlocked: userStats.countriesVisited.length >= 50 },
            ],
          },
          {
            id: 'cities',
            name: 'City Explorer',
            description: 'Explore cities worldwide',
            icon: 'business',
            tiers: [
              { tier: 'bronze', target: 10, current: userStats.citiesVisited.length, unlocked: userStats.citiesVisited.length >= 10 },
              { tier: 'silver', target: 25, current: userStats.citiesVisited.length, unlocked: userStats.citiesVisited.length >= 25 },
              { tier: 'gold', target: 50, current: userStats.citiesVisited.length, unlocked: userStats.citiesVisited.length >= 50 },
              { tier: 'platinum', target: 100, current: userStats.citiesVisited.length, unlocked: userStats.citiesVisited.length >= 100 },
            ],
          },
          {
            id: 'continents',
            name: 'Global Citizen',
            description: 'Visit all continents',
            icon: 'earth',
            tiers: [
              { tier: 'bronze', target: 1, current: userStats.continentsVisited.length, unlocked: userStats.continentsVisited.length >= 1 },
              { tier: 'silver', target: 3, current: userStats.continentsVisited.length, unlocked: userStats.continentsVisited.length >= 3 },
              { tier: 'gold', target: 5, current: userStats.continentsVisited.length, unlocked: userStats.continentsVisited.length >= 5 },
              { tier: 'platinum', target: 7, current: userStats.continentsVisited.length, unlocked: userStats.continentsVisited.length >= 7 },
            ],
          },
          {
            id: 'days',
            name: 'Days Abroad',
            description: 'Accumulate travel days',
            icon: 'calendar',
            tiers: [
              { tier: 'bronze', target: 30, current: userStats.totalTripDays, unlocked: userStats.totalTripDays >= 30 },
              { tier: 'silver', target: 100, current: userStats.totalTripDays, unlocked: userStats.totalTripDays >= 100 },
              { tier: 'gold', target: 365, current: userStats.totalTripDays, unlocked: userStats.totalTripDays >= 365 },
              { tier: 'platinum', target: 1000, current: userStats.totalTripDays, unlocked: userStats.totalTripDays >= 1000 },
            ],
          },
          {
            id: 'streak',
            name: 'Travel Streak',
            description: 'Consecutive months with trips',
            icon: 'flame',
            tiers: [
              { tier: 'bronze', target: 3, current: userStats.currentStreak, unlocked: userStats.currentStreak >= 3 },
              { tier: 'silver', target: 6, current: userStats.currentStreak, unlocked: userStats.currentStreak >= 6 },
              { tier: 'gold', target: 12, current: userStats.currentStreak, unlocked: userStats.currentStreak >= 12 },
              { tier: 'platinum', target: 24, current: userStats.currentStreak, unlocked: userStats.currentStreak >= 24 },
            ],
          },
        ];

        setMilestones(calculatedMilestones);
      }
    } catch (error) {
      console.error('[MilestonesView] Error loading milestones:', error);
      setMilestonesError("Couldn't load milestones. Check your connection and try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadMilestones();
  };

  const getTierColor = (tier: MilestoneTier): string => {
    switch (tier) {
      case 'bronze':
        return '#CD7F32';
      case 'silver':
        return '#C0C0C0';
      case 'gold':
        return '#FFD700';
      case 'platinum':
        return '#E5E4E2';
      default:
        return theme.colors.accent;
    }
  };

  const getTierGradient = (tier: MilestoneTier): readonly [string, string, ...string[]] => {
    switch (tier) {
      case 'bronze':
        return ['#CD7F32', '#B8732A', '#A06622'];
      case 'silver':
        return ['#C0C0C0', '#A8A8A8', '#909090'];
      case 'gold':
        return ['#FFD700', '#FFC700', '#FFB700'];
      case 'platinum':
        return ['#E5E4E2', '#D4D3D1', '#C3C2C0'];
      default:
        return [theme.colors.accent, theme.colors.primary];
    }
  };

  const getNextTier = (milestone: Milestone): { tier: MilestoneTier; target: number } | null => {
    const nextTier = milestone.tiers.find(t => !t.unlocked);
    if (!nextTier) return null;
    return { tier: nextTier.tier, target: nextTier.target };
  };

  if (loading && !stats) {
    return (
      <Screen>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
        </View>
      </Screen>
    );
  }

  if (milestonesError && !stats) {
    return (
      <Screen>
        <ErrorState
          variant="full"
          title="Couldn't load milestones"
          message={milestonesError}
          onRetry={loadMilestones}
          retryLabel="Retry"
        />
      </Screen>
    );
  }

  return (
    <Screen
      title="Milestones"
      subtitle="Track your progress toward travel goals"
      headerAction={
        <Pressable 
          onPress={handleClose}
          style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
        >
          <Ionicons name="close" size={24} color={theme.colors.text} />
        </Pressable>
      }
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >

        {milestonesError ? (
          <View style={{ marginBottom: 16 }}>
            <ErrorState
              title="Couldn't refresh milestones"
              message={milestonesError}
              onRetry={loadMilestones}
              retryLabel="Retry"
            />
          </View>
        ) : null}

        {milestones.map((milestone) => {
          const nextTier = getNextTier(milestone);
          const highestUnlocked = milestone.tiers
            .filter(t => t.unlocked)
            .sort((a, b) => {
              const order = { bronze: 1, silver: 2, gold: 3, platinum: 4 };
              return order[b.tier] - order[a.tier];
            })[0];

          return (
            <Card
              key={milestone.id}
              variant="elevated"
              elevation={1}
              padding={16}
              style={styles.milestoneCard}
            >
              <View style={styles.milestoneHeader}>
                <Heading style={styles.milestoneName}>{milestone.name}</Heading>
                <Body style={{ color: theme.colors.textSecondary }}>
                  {milestone.description}
                </Body>
              </View>

              {/* Tier Badges */}
              <View style={styles.tiersContainer}>
                {milestone.tiers.map((tierData) => {
                  const isUnlocked = tierData.unlocked;
                  const isHighest = highestUnlocked?.tier === tierData.tier;
                  const tierColor = getTierColor(tierData.tier);

                  return (
                    <View
                      key={tierData.tier}
                      style={[
                        styles.tierBadge,
                        isUnlocked && {
                          backgroundColor: tierColor + '20',
                          borderColor: tierColor,
                          borderWidth: isHighest ? 3 : 1,
                        },
                        !isUnlocked && {
                          opacity: 0.3,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.tierIcon,
                          { backgroundColor: isUnlocked ? tierColor : theme.colors.background },
                        ]}
                      >
                        {isUnlocked && (
                          <View style={styles.checkmark}>
                            <View style={[styles.checkmarkInner, { backgroundColor: tierColor }]} />
                          </View>
                        )}
                      </View>
                      <Caption
                        style={[
                          styles.tierLabel,
                          { color: isUnlocked ? tierColor : theme.colors.textTertiary },
                        ]}
                      >
                        {tierData.tier}
                      </Caption>
                      <Body
                        style={[
                          styles.tierTarget,
                          { color: isUnlocked ? tierColor : theme.colors.textTertiary },
                        ]}
                      >
                        {tierData.target}
                      </Body>
                    </View>
                  );
                })}
              </View>

              {/* Progress to Next Tier */}
              {nextTier && (() => {
                const currentTierData = milestone.tiers.find(t => t.tier === nextTier.tier);
                const current = currentTierData?.current || 0;
                const target = nextTier.target;
                const progressPercentage = Math.min((current / target) * 100, 100);
                const tierGradient = getTierGradient(nextTier.tier);
                
                return (
                  <View style={styles.progressContainer}>
                    <View style={styles.progressHeader}>
                      <Body style={{ color: theme.colors.textSecondary }}>
                        Progress to {nextTier.tier}
                      </Body>
                      <View style={styles.progressStats}>
                        <Body style={{ fontWeight: '600' }}>
                          {current} / {target}
                        </Body>
                        <Body style={[styles.progressPercentage, { color: getTierColor(nextTier.tier) }]}>
                          {Math.round(progressPercentage)}%
                        </Body>
                      </View>
                    </View>
                    <View style={styles.progressBar}>
                      <LinearGradient
                        colors={tierGradient}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={[
                          styles.progressFill,
                          {
                            width: `${progressPercentage}%`,
                          },
                        ]}
                      />
                    </View>
                  </View>
                );
              })()}

              {!nextTier && (
                <View style={styles.completedBadge}>
                  <Body style={{ color: theme.colors.success, fontWeight: '600' }}>
                    All tiers unlocked! 🎉
                  </Body>
                </View>
              )}
            </Card>
          );
        })}

        {!stats && (
          <Card variant="elevated" elevation={1} padding={24} style={styles.emptyState}>
            <Body style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
              Complete your first trip to start earning milestones!
            </Body>
          </Card>
        )}
      </ScrollView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  milestoneCard: {
    marginBottom: 16,
  },
  milestoneHeader: {
    marginBottom: 16,
  },
  milestoneName: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  tiersContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 16,
  },
  tierBadge: {
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    minWidth: 70,
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  tierIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
    position: 'relative',
  },
  checkmark: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  checkmarkInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  tierLabel: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  tierTarget: {
    fontSize: 14,
    fontWeight: '600',
  },
  progressContainer: {
    marginTop: 8,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  progressStats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  progressPercentage: {
    fontWeight: '700',
    fontSize: 14,
  },
  progressBar: {
    height: 16, // Increased from 8
    backgroundColor: '#E0E0E0',
    borderRadius: 8, // Increased from 4
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 8, // Increased from 4
  },
  completedBadge: {
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#E8F5E9',
    alignItems: 'center',
  },
  emptyState: {
    marginTop: 24,
  },
});

